/* eslint-disable react/display-name */
import React, { memo, useMemo } from 'react';
import { t as translate } from 'ttag';
import { TextField } from '@material-ui/core';
import { isEmpty } from 'lodash';

import Autocomplete from '@material-ui/lab/Autocomplete';
import { useConfirmation } from '@coolremote/shared';

import useStyles from './styles';
import clsx from 'clsx';
import { bacnetTypeIdToShortName } from '@constants/bacnet';

/**
 * Extracts and returns the object type from a given data point's shortId.
 *
 * @param {Object} dataPoint - The data point object containing a shortId property.
 * @param {string} [dataPoint.shortId=''] - The shortId string in the format "type:otherData".
 * @returns {number} - The parsed integer representing the object type.
 */
const getObjectType = (dataPoint) => {
  const {shortId = ''} = dataPoint;
  return parseInt(shortId.split(':')[0]);
};

/**
 * Retrieves a short name representation for a given BACnet type.
 *
 * @param {string} type - The BACnet type identifier.
 * @returns {string} The short name enclosed in square brackets if the type is valid,
 *                   otherwise an empty string.
 */
const getShortName = (type) => {
  if(!bacnetTypeIdToShortName[type]){
    return '';
  }

  return `[${bacnetTypeIdToShortName[type]}]`;
};

// Separate the data points list into its own component
const DataPointsList = memo(
  ({
    onDragDataPoint,
    paramTemplates,
    onClickDataPoint,
    selectedParamTemplate,
    onSelectParamTemplate,
    selectedDataPoint,
    addedDataPoints = {},
  }) => {
    const confirm = useConfirmation();
    const classes = useStyles();

    const paramTemplateData = useMemo(() => {

      const templateData = paramTemplates[selectedParamTemplate]?.data || {};

      const templatesList = Object.keys(templateData);

      return templatesList.map((dataPointId) => {
        const dataPoint = paramTemplates[selectedParamTemplate].data[dataPointId];
        return {
          id: dataPointId,
          objectType:getObjectType(dataPoint),
          ...dataPoint,
        };
      });

    }, [paramTemplates, selectedParamTemplate]);

    const handleSelectParamTemplate = async (newValue) => {
      let result = true;
      if (selectedParamTemplate) {
        result = await confirm({
          title: translate`Confirm changing param template?`,
          bodyMessage: translate`Changing param template will reset control mapping?`,
          confirmText: translate`Confirm`,
          cancelText: translate`Cancel`,
        });
      }

      if (result) {
        onSelectParamTemplate(newValue.id);
      }
    };

    const handleNativeDrag = (point) => () => {
      onDragDataPoint({point});
    };

    const handleDataPointClick = (dataPoint) => () => {
      if(addedDataPoints[dataPoint.id]){
        onClickDataPoint(dataPoint.id);
      }
    };

    return (
      <div className={classes.dataPoints}>
        <div className={classes.dataPoints__section}>
          <div className={classes.dataPoints__section__instructions}>
            {translate`Parameter Template`}
          </div>
          <div
            className={classes.dataPoints__section__content}
            style={{
              padding: '0 16px',
            }}
          >
            {Object.keys(paramTemplates).length > 0 && (
              <Autocomplete
                className={classes.customAutocompleteField}
                fullWidth
                options={Object.values(paramTemplates)}
                getOptionLabel={(option) => option.name}
                onChange={(_, newValue) => {
                  handleSelectParamTemplate(newValue);
                }}
                value={paramTemplates[selectedParamTemplate]}
                disableClearable={true}
                renderInput={(params) => (
                  <TextField
                    fullWidth
                    placeholder={translate`Select Template`}
                    {...params}
                    variant="outlined"
                    className={classes.customTextField}
                    InputProps={{
                      ...params.InputProps,
                    }}
                  />
                )}
              />
            )}
          </div>
        </div>
        {!isEmpty(paramTemplateData) && (
          <div className={clsx(classes.dataPoints__section, 'list-section')}>
            <div className={classes.dataPoints__section_List_instructions}>
              {translate`Parameter List`}
            </div>
            <div className={classes.dataPoints__list}>
              {paramTemplateData.map((dataPoint) => (
                <div
                  draggable={!addedDataPoints[dataPoint.id]}
                  onDrag={handleNativeDrag(dataPoint)}
                  onClick={handleDataPointClick(dataPoint)}
                  key={dataPoint.id}
                  title={dataPoint.name}
                  className={clsx(classes.dataPoints__listItem, {
                    isSelected: selectedDataPoint === dataPoint.id,
                    draggable: !addedDataPoints[dataPoint.id],
                  })}
                >
                  <span className='item-name'>{dataPoint.name}</span>
                  <span>{getShortName(dataPoint.objectType)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default DataPointsList;
